import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { LoginModal } from "../components/LoginModal";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginReason, setLoginReason] = useState(null);
  const pendingRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const startLogin = useCallback(() => {
    sessionStorage.setItem("lbs_return", window.location.pathname + window.location.search);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Open the login modal for a gated (premium) reason. After sign-in the
  // subscription/welcome modal is shown (via sessionStorage intent).
  const promptLogin = useCallback((reason = null) => {
    setLoginReason(reason);
    sessionStorage.setItem("lbs_after_login", "upgrade");
    setLoginOpen(true);
  }, []);

  const requireAuth = useCallback(
    (fn) => {
      if (user) {
        fn && fn();
        return;
      }
      pendingRef.current = fn;
      setLoginReason(null);
      sessionStorage.setItem("lbs_after_login", "upgrade");
      setLoginOpen(true);
    },
    [user]
  );

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    setUser(null);
    // Reset to a fresh, logged-out home experience.
    window.location.href = "/";
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, requireAuth, promptLogin, logout, startLogin, refreshUser: checkAuth }}>
      {children}
      <LoginModal
        open={loginOpen}
        reason={loginReason}
        onClose={() => setLoginOpen(false)}
        onGoogle={() => {
          setLoginOpen(false);
          startLogin();
        }}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
